/**
 * Cart management singleton with Shopify Cart API integration
 * Handles all cart operations including add, update, remove, and sync with mini-cart UI
 */

import { VisibilityElement } from '../custom-elements/visibility'
import { NativeFetcher } from './fetch'
import { globalState } from './utils'
import { CartRequestItem, Cart as CartType, ChangeItem, UpdateCartRequest } from './cart.types'
import { PWP } from './product-with-purchase'
import { ShopifySectionHelper } from './section-render'

interface CustomCartEvent extends Event {
  detail: {
    count?: number
    action?: boolean
  }
}

interface ShopifySectionEvent extends Event {
  detail: {
    sectionId?: string
    blockId?: string
    load?: boolean
  }
}

export class Cart extends NativeFetcher {
  // eslint-disable-next-line no-use-before-define
  static instance: Cart | null = null

  // Node IDs
  static ID__MINI_CART = 'mini-cart'

  // DOM Elements IDS
  static CLASS__CART_COUNT = '.cart-count'
  static DATA__CART_COUNT = 'data-item-count'

  // Endpoints
  static ENDPOINT__GET_CART = globalState?.routes?.cart_url + '?view=ajax'
  static ENDPOINT__CLEAR_CART = globalState?.routes?.cart_clear_url + '.js'
  static ENDPOINT__ADD_LINE_ITEMS = globalState?.routes?.cart_add_url + '.js'
  static ENDPOINT__UPDATE_LINE_ITEMS = globalState?.routes?.cart_update_url + '.js'
  static ENDPOINT__CHANGE_LINE_ITEM = globalState?.routes?.cart_change_url + '.js'

  static SHOPIFY__SECTION_SELECT = 'shopify:section:select'
  static SHOPIFY__SECTION_ID = /sections--\d+__mini-cart$/

  // Instance variables
  value: CartType | undefined // The actual cart
  private static sidecartAutoCloseTimeout: ReturnType<typeof setTimeout> | null = null

  constructor() {
    if (Cart.instance) return Cart.instance
    super()
    Cart.instance = this
    /** Add Event Listeners */
    this.addEventListeners()
    /** On initialisation get the cart from shopify
     * set the instance variable 'value' with the current
     * customer cart.
     */
    this.getCart()
    this.value = (globalState?.cart?.initCart as CartType) || undefined
  }

  // Static Cart Handlers
  static EVENT_UPDATE_CART_COUNT = 'updateCartCount'
  static dispatchUpdateCartCount(count: number) {
    document.dispatchEvent(
      new CustomEvent(Cart.EVENT_UPDATE_CART_COUNT, {
        detail: {
          count
        }
      })
    )
  }

  // Static Cart Handlers
  static EVENT_RENDER_MINI_CART = 'renderMiniCart'
  static EVENT_RENDER_CART_PAGE = 'renderCartPage'
  static EVENT_EXTERNAL_OPEN_MINI_CART = 'externalOpenMiniCart'

  /** Section instance id of the cart-main section in templates/cart.json.
   * Must be the template instance id (not the section filename) so the
   * Section Rendering API includes the section's settings and blocks. */
  static CART_PAGE_SECTION_ID = 'main'

  static skipNextCartPageRefresh = false

  static dispatchRenderMiniCart(action: boolean) {
    document.dispatchEvent(
      new CustomEvent(Cart.EVENT_RENDER_MINI_CART, {
        detail: {
          action
        }
      })
    )
    Cart.dispatchRenderCartPage()
  }

  static dispatchRenderCartPage() {
    document.dispatchEvent(new CustomEvent(Cart.EVENT_RENDER_CART_PAGE))
  }

  addEventListeners() {
    document.addEventListener(Cart.EVENT_UPDATE_CART_COUNT, this.handleCartCount)
    document.addEventListener(Cart.EVENT_RENDER_MINI_CART, this.handleMiniCartRender)
    document.addEventListener(Cart.EVENT_RENDER_CART_PAGE, this.handleCartPageRender)
    document.addEventListener(Cart.SHOPIFY__SECTION_SELECT, this.handleSectionSelect)
    window.addEventListener(Cart.EVENT_EXTERNAL_OPEN_MINI_CART, () => {
      Cart.dispatchRenderMiniCart(true)
      Cart.dispatchOpenMiniCart()
    })
  }

  handleSectionSelect(event: Event) {
    const customEvent = event as ShopifySectionEvent
    const { sectionId } = customEvent.detail
    if (!sectionId) return
    const match = sectionId.match(Cart.SHOPIFY__SECTION_ID)
    if (!match) {
      Cart.dispatchHideMiniCart()
      return
    }
    Cart.dispatchOpenMiniCart()
  }

  static dispatchOpenMiniCart() {
    VisibilityElement.dispatchShow(Cart.ID__MINI_CART)
  }

  dispatchOpenMiniCart() {
    Cart.dispatchOpenMiniCart()
  }

  static dispatchHideMiniCart() {
    VisibilityElement.dispatchHide(Cart.ID__MINI_CART)
  }

  private static scheduleSidecartAutoClose() {
    const timeoutMs = globalState?.cart?.sidecartAutoCloseTimeout
    if (globalState?.cart?.minicartType !== 'sidecart' || !timeoutMs || timeoutMs <= 0) return
    if (Cart.sidecartAutoCloseTimeout) clearTimeout(Cart.sidecartAutoCloseTimeout)
    Cart.sidecartAutoCloseTimeout = setTimeout(() => {
      Cart.dispatchHideMiniCart()
      Cart.sidecartAutoCloseTimeout = null
    }, timeoutMs)
  }

  dispatchHideMiniCart() {
    Cart.dispatchHideMiniCart()
  }

  // API CALLS - 1:1 with Shopify Docs

  async getCart() {
    this.value = await this.get(Cart.ENDPOINT__GET_CART)
    if (this.value) {
      Cart.dispatchUpdateCartCount(this.value?.item_count || 0)
    }
    return this.value
  }

  /**
   * Adds line item to cart asynchronously
   * Can pass 1 line item or many
   * https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-add-js
   */
  async addLineItems(items: CartRequestItem[], options = { silent: false }) {
    const { silent = false } = options

    const body = {
      items: items.length ? items : [items]
    }

    const data = await this.post(Cart.ENDPOINT__ADD_LINE_ITEMS, body)
    if (data && !silent && !data.error) {
      PWP.showProductWithPurchase(data.items?.[0] ?? data)
      Cart.dispatchOpenMiniCart()
      Cart.scheduleSidecartAutoClose()
    }
    Cart.dispatchRenderMiniCart(true)

    return data
  }

  /**
   * Update line item in cart asynchronously
   * Can pass 1 line item or many
   * https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-update-js
   */
  async updateLineItems(items: UpdateCartRequest['updates'], options = { silent: true }) {
    const { silent = true } = options

    const body = {
      updates: items
    }
    const data = await this.post(Cart.ENDPOINT__UPDATE_LINE_ITEMS, body)
    if (data && !silent) Cart.dispatchOpenMiniCart()
    Cart.dispatchUpdateCartCount(data.item_count)
    Cart.dispatchRenderCartPage()
    return data
  }

  /**
   * Changes line item in cart asynchronously
   * Can only update 1 at a time
   * https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-change-js
   */

  async changeLineItem(item: ChangeItem, options = { silent: true }) {
    const { silent = true } = options

    const data = await this.post(Cart.ENDPOINT__CHANGE_LINE_ITEM, item)
    if (data && !silent && !data.error) {
      Cart.dispatchOpenMiniCart()
    }
    Cart.dispatchUpdateCartCount(data.item_count)
    Cart.dispatchRenderMiniCart(true)
    return data
  }

  /**
   * Clears Cart
   * Can only update 1 at a time
   * https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-change-js
   */

  async clearCart(options = { silent: true }) {
    const { silent = true } = options

    const data = await this.post(Cart.ENDPOINT__CLEAR_CART, {})
    if (data && !silent) Cart.dispatchOpenMiniCart()
    Cart.dispatchRenderCartPage()
    return data
  }

  // Auxillary API handlers - wrappers for additional common cases

  async removeLineItem(id: string | number, options: { silent: boolean }) {
    const items = {
      [id]: 0
    }
    return await this.updateLineItems(items, options)
  }

  async updateLineItemQuantity(item: CartRequestItem, options: { silent: boolean }) {
    const { id, quantity } = item
    const items = {
      [id]: quantity
    }

    return await this.updateLineItems(items, options)
  }

  handleCartCount(event: Event) {
    const customEvent = event as CustomCartEvent
    const cartCountDOMElements = document.querySelectorAll(Cart.CLASS__CART_COUNT)
    if (!cartCountDOMElements.length) return
    /** Get item count  */
    const { count } = customEvent.detail
    if (count === undefined) {
      console.error('Cart count not provided')
      return
    }

    const countStr = count.toString()
    for (let i = 0; i < cartCountDOMElements.length; i++) {
      const cartCountDOMElement = cartCountDOMElements[i]
      if (cartCountDOMElement.getAttribute(Cart.DATA__CART_COUNT) === countStr) continue

      cartCountDOMElement.setAttribute(Cart.DATA__CART_COUNT, countStr)
      cartCountDOMElement.innerHTML = countStr
    }
  }

  /**
   * Handles the mini-cart render
   * The mini-cart preact component contains a useUrl hook
   * that will re-render the component when the url changes updates.
   *
   */
  handleMiniCartRender() {
    const url = new URL(window.location.href)
    /** Add the action query param.
     * 'action=1' has no purpose other than a placeholder value
     * when triggering the mini-cart preact component.
     */
    url.searchParams.set('action', '1')
    // Push the new URL to history
    window.history.pushState({}, '', url.toString())

    /** Push back to trigger the popstate event listener. */
    window.history.back()
  }

  /**
   * Refreshes #cart-items-inner and #cart-summary on the cart page when cart is updated.
   * Skips when quantity-input or cart-line-variant-selector set skipNextCartPageRefresh.
   */
  handleCartPageRender() {
    if (Cart.skipNextCartPageRefresh) {
      Cart.skipNextCartPageRefresh = false
      return
    }

    const cartContainer = document.getElementById('cart-container')
    if (!cartContainer || !globalState?.routes?.cart_url) return

    const pathname = window.location.pathname.replace(/\/$/, '') || '/'
    const cartPath = (globalState.routes.cart_url || '/cart').replace(/^\//, '').replace(/\/$/, '')
    const cartPathNorm = `/${cartPath}`
    if (pathname !== cartPathNorm) return

    ShopifySectionHelper.fetchAndUpdateSection({
      sectionId: Cart.CART_PAGE_SECTION_ID,
      baseUrl: globalState.routes.cart_url,
      sectionIdFallbacks: ['cart-main'],
      fetchSelectors: ['#cart-items-inner', '#cart-summary'],
      targetSelectors: ['#cart-items-inner', '#cart-summary']
    }).then(() => {
      if (typeof (window as any).lazyLoadInstance?.update === 'function') {
        (window as any).lazyLoadInstance.update()
      }
      requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'))
      })
    }).catch((err) => {
      console.error('Cart page refresh failed:', err)
    })
  }
}

export const cart = new Cart()
